import React from 'react'

import beevelopeWordmarkLight from '@/assets/Beevelope_Light.svg'
import beevelopeWordmarkDark from '@/assets/Beevelope_Dark.svg'
import logomarkLight from '@/assets/Light_Logomark.svg'
import logomarkDark from '@/assets/Dark_Logomark.svg'

/**
 * Brand assets, rendered theme-aware.
 *
 * The SVGs in `src/assets/` come in two fixed-color variants:
 * `*_Light` (near-black #08090A marks, for light backgrounds) and `*_Dark`
 * (near-white #F7F8F8 marks, for dark backgrounds), both sharing the
 * #5E6AD2 accent. Since the files carry their own colors (no
 * `currentColor`), theming is done by rendering both `<img>`s and letting
 * Tailwind's `dark:` classes pick the visible one — same class-on-<html>
 * strategy the rest of the app uses.
 *
 * Accessibility: when the logo sits inside a link/button that already has
 * an `aria-label`, pass `alt=""` so it stays decorative; otherwise give it
 * (or keep) a real `alt`.
 */

interface ThemedBrandImgProps {
  lightSrc: string
  darkSrc: string
  height: number
  alt: string
  className?: string
}

const ThemedBrandImg: React.FC<ThemedBrandImgProps> = ({
  lightSrc,
  darkSrc,
  height,
  alt,
  className,
}) => (
  <span
    className={`inline-flex shrink-0 ${className ?? ''}`}
    aria-hidden={alt === '' ? true : undefined}
  >
    <img
      src={lightSrc}
      alt={alt}
      style={{ height, width: 'auto' }}
      className="dark:hidden"
      draggable={false}
    />
    <img
      src={darkSrc}
      alt={alt}
      style={{ height, width: 'auto' }}
      className="hidden dark:block"
      draggable={false}
    />
  </span>
)

export interface LogomarkProps {
  /** Rendered height in px — width scales with the mark's aspect ratio. */
  size?: number
  className?: string
  /** Empty string (default) keeps it decorative; see module docstring. */
  alt?: string
}

/** The envelope logomark alone — for tight spots with no room for the wordmark. */
export const Logomark: React.FC<LogomarkProps> = ({ size = 18, className, alt = '' }) => (
  <ThemedBrandImg
    lightSrc={logomarkLight}
    darkSrc={logomarkDark}
    height={size}
    alt={alt}
    className={className}
  />
)

export interface BrandWordmarkProps {
  /** Rendered height in px — width scales with the wordmark's aspect ratio. */
  height?: number
  className?: string
  alt?: string
}

/** The full "Beevelope" lockup (logomark + name). */
export const BrandWordmark: React.FC<BrandWordmarkProps> = ({
  height = 24,
  className,
  alt = 'Beevelope',
}) => (
  <ThemedBrandImg
    lightSrc={beevelopeWordmarkLight}
    darkSrc={beevelopeWordmarkDark}
    height={height}
    alt={alt}
    className={className}
  />
)

export default Logomark
